import React from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { getLanding, landingPages } from '@/content/pages';
import { renderLanding, renderRelated, landingUrl, SERVICE, type LandingKind } from '@/lib/landingFormat';
import { CALENDLY_URL } from '@/lib/site';
import NotFound from './NotFound';

/**
 * One template for every city and industry page. The copy is markdown in
 * src/content/pages; renderLanding() produces the same HTML the prerenderer
 * writes, so crawlers and visitors see identical content.
 *
 * city-web pages keep their historical /city-<slug> URLs, which arrive here
 * through the "/:slug" route, hence the prefix check.
 */
const LandingPage = ({ kind }: { kind: LandingKind }) => {
  const { slug = '' } = useParams();
  const key = kind === 'city-web' ? (slug.startsWith('city-') ? slug.slice(5) : '') : slug;
  const page = key ? getLanding(kind, key) : undefined;
  if (!page) return <NotFound />;

  const svc = SERVICE[kind];
  return (
    <div className="min-h-screen pt-16 bg-gradient-to-b from-white to-slate-100 dark:from-slate-900 dark:to-slate-950">
      <Helmet>
        <title>{page.metaTitle}</title>
        <meta name="description" content={page.description} />
        <link rel="canonical" href={landingUrl(page)} />
      </Helmet>
      <div className="container-custom py-12 max-w-3xl mx-auto">
        <article
          className="landing-article prose prose-lg prose-slate dark:prose-invert max-w-none prose-headings:scroll-mt-24 prose-a:text-primary"
          dangerouslySetInnerHTML={{ __html: renderLanding(page) }}
        />
        <div className="mt-12 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-8 text-center">
          <h2 className="text-2xl font-bold">{page.city ? `Working in ${page.city}?` : 'Want this set up for your business?'}</h2>
          <p className="mt-2 text-slate-600 dark:text-slate-300">
            Book a free 30-minute call. We'll look at what you have and tell you honestly what we'd change.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Button asChild size="lg" className="rounded-full">
              <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">Book a free call</a>
            </Button>
            <Button asChild size="lg" variant="outline" className="rounded-full">
              <a href={svc.path}>{kind === 'industry' ? 'See all industries' : `About our ${svc.name.toLowerCase()}`}</a>
            </Button>
          </div>
        </div>
        <div
          className="mt-12 prose prose-slate dark:prose-invert max-w-none prose-a:text-primary"
          dangerouslySetInnerHTML={{ __html: renderRelated(page, landingPages) }}
        />
      </div>
    </div>
  );
};

export default LandingPage;
